import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiArrowLeft, FiDownload, FiMail, FiEdit2, FiTrash2,
  FiFileText,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { getReceipt, cancelReceipt, sendReceiptEmail } from '../../services/api';
import { PageHeader, CancelConfirmModal } from '../../components/common';
import OnlinePaymentWarningModal from './OnlinePaymentWarningModal';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { usePdfDownload } from '../../pdf/usePdfDownload';
import { fmtCurrency } from '../../utils/formatNumber';
import { formatDate } from '../../utils/dateUtils';
import { TOAST, ACTIONS, MODAL } from '../../constants/labels';
import { PAYMENT_LABEL } from './receiptsConfig';
import styles from './ReceiptDetailPage.module.css';

const TYPE_LABELS = {
  standard:  'Standard Donation',
  gold:      'Gold',
  anonymous: 'Shah A/C (Walk-in)',
  online:    'Online',
  item:      'Item',
  ghee_boli: 'Ghee Boli',
  bhandar:   'Bhandar',
};

const REFERENCE_LABEL = {
  cheque:        'Cheque No',
  upi:           'UPI Ref No',
  bank_transfer: 'Bank Ref No',
};

export default function ReceiptDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDerasarAdmin, activeDerasar } = useAuth();

  const [receipt, setReceipt] = useState(null);
  const [pledgeSettlements, setPledgeSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlineWarningItem, setOnlineWarningItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getReceipt(id);
      setReceipt(res.data.receipt);
      setPledgeSettlements(res.data.pledgeSettlements || []);
    } catch {
      toast.error('Receipt not found');
      navigate('/accounting/receipts');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const { download: downloadPdf, downloading } = usePdfDownload('receipt');

  const handleSendEmail = useCallback(async (item) => {
    try {
      const res = await sendReceiptEmail(item.id);
      toast.success(res.data?.message || 'Email sent successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send email');
    }
  }, []);

  const { cancelModal, cancelReason, cancelling, openCancel, closeCancel, setReason, handleCancel } =
    useCancelConfirm(cancelReceipt, () => navigate(-1), TOAST.deleteSuccess(PAYMENT_LABEL), { requireReason: false });

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!receipt) return null;

  const receiptTypeLabel = TYPE_LABELS[receipt.receiptType] || receipt.receiptType;
  const paymentModeLabel = receipt.paymentMode ? receipt.paymentMode.replaceAll('_', ' ') : '';
  const showPaymentModeBadge = paymentModeLabel
    && paymentModeLabel.toLowerCase() !== receiptTypeLabel.toLowerCase();

  const memberName = receipt.receiptType === 'anonymous'
    ? (receipt.donorName || 'Anonymous')
    : (receipt.member?.name || '—');

  const totalSettled = pledgeSettlements.reduce((sum, settlement) => sum + (parseFloat(settlement.settledAmount) || 0), 0);

  return (
    <div className={styles.page}>
      <PageHeader
        icon={<FiFileText />}
        title="Receipt Detail"
        actions={(
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Cancel
          </button>
        )}
      />

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className={styles.headerBand}>
          <span className={styles.receiptNo}>{receipt.receiptNo}</span>
          <div className={styles.badgeRow}>
            <span className={styles.typeBadge}>{receiptTypeLabel}</span>
            {showPaymentModeBadge && (
              <span className={styles.typeBadge} style={{ background: 'rgba(255,255,255,0.1)' }}>
                {paymentModeLabel}
              </span>
            )}
          </div>
        </div>

        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Member</span>
            <span className={styles.infoValue}>{memberName}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Amount</span>
            <span className={styles.amountValue}>{fmtCurrency(receipt.amountInr)}</span>
          </div>
          {receipt.receiptType === 'gold' && (
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Gold Grams</span>
              <span className={styles.infoValue}>{receipt.goldGrams ? `${receipt.goldGrams} g` : '—'}</span>
            </div>
          )}
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Date</span>
            <span className={styles.infoValue}>{formatDate(receipt.receiptDate)}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Financial Year</span>
            <span className={styles.infoValue}>{receipt.FinancialYear?.yearLabel || '—'}</span>
          </div>
          {receipt.manualReceiptNo && (
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Manual Receipt No</span>
              <span className={styles.infoValue}>{receipt.manualReceiptNo}</span>
            </div>
          )}
          {receipt.referenceNo && (
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>{REFERENCE_LABEL[receipt.paymentMode] || 'Reference No'}</span>
              <span className={styles.infoValue}>{receipt.referenceNo}</span>
            </div>
          )}
          {receipt.narration && (
            <div className={styles.infoItem} style={{ gridColumn: '1 / -1' }}>
              <span className={styles.infoLabel}>Narration</span>
              <span className={styles.infoValue}>{receipt.narration}</span>
            </div>
          )}
        </div>

        {pledgeSettlements.length > 0 && (
          <>
            <div className={styles.divider} />
            <div className={styles.settlementsSection}>
              <div className={styles.sectionTitle}>Offerings Settled</div>
              <table className={styles.miniTable}>
                <thead>
                  <tr>
                    <th>Donation Category</th>
                    <th>Offering Date</th>
                    <th>Offered (Rs)</th>
                    <th>Settled (Rs)</th>
                  </tr>
                </thead>
                <tbody>
                  {pledgeSettlements.map((settlement) => (
                    <tr key={settlement.offeringId}>
                      <td>{settlement.offering?.DonationCategory?.name || '—'}</td>
                      <td>{settlement.offering?.offeringDate ? formatDate(settlement.offering.offeringDate) : '—'}</td>
                      <td>{fmtCurrency(settlement.offering?.offeredAmount)}</td>
                      <td>{fmtCurrency(settlement.settledAmount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}>Total Settled</td>
                    <td>{fmtCurrency(totalSettled)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}

        <div className={styles.actionBar}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => downloadPdf({ receipt, derasar: activeDerasar?.derasarId, member: receipt?.member })}
            disabled={downloading}
          >
            <FiDownload /> {downloading ? 'Generating…' : ACTIONS.download}
          </button>
          {receipt.userId && (
            <button className="btn btn-ghost btn-sm" onClick={() => handleSendEmail(receipt)} disabled={downloading}>
              <FiMail /> {ACTIONS.email}
            </button>
          )}
          {isDerasarAdmin && receipt.receiptType !== 'online' && (
            <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/accounting/receipts/${receipt.id}/edit`)}>
              <FiEdit2 /> {ACTIONS.edit}
            </button>
          )}
          {isDerasarAdmin && (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => {
                if (receipt.receiptType === 'online') {
                  setOnlineWarningItem(receipt);
                } else {
                  openCancel(receipt);
                }
              }}
            >
              <FiTrash2 /> {ACTIONS.delete}
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Back
          </button>
        </div>
      </div>

      <OnlinePaymentWarningModal
        item={onlineWarningItem}
        onClose={() => setOnlineWarningItem(null)}
        onConfirm={(item) => {
          setOnlineWarningItem(null);
          openCancel(item);
        }}
      />

      <CancelConfirmModal
        entity={PAYMENT_LABEL}
        item={cancelModal}
        itemDisplay={cancelModal?.receiptNo}
        amount={cancelModal?.amountInr}
        extraNote={MODAL.cancel.journalEntryNote}
        reason={cancelReason}
        onReasonChange={setReason}
        onConfirm={handleCancel}
        onClose={closeCancel}
        cancelling={cancelling}
        showReason={false}
        title="Delete Receipt"
        confirmLabel="Delete"
      />
    </div>
  );
}
